import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { FileText, Pencil, Send, MapPin, Calendar as CalendarIcon, Plus, Eye, Download, Trash2, Mail, Clock, Power, PowerOff, Building2, Search, Filter, FileBarChart, Hourglass, CalendarX, RotateCcw, AlertTriangle, Monitor, Image as ImageIcon, MoveRight, X, Pen, ShieldCheck, Copy, CheckCircle, ArrowRight, MoreHorizontal, Paperclip } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DevisLignesMulti from '../components/DevisLignesMulti';
import { fetchEmplacementsByType } from '../lib/emplacementsHelper';
import { toast } from 'sonner';
import { ContratTvaToggle } from '../components/ContratTvaToggle';

/**
 * Parse une durée en nombre de jours en tenant compte du type de contrat.
 * - duree = nombre (ex: 1, 3, 90)
 * - typeContrat = 'Mensuel' / 'Trimestriel' / 'Annuel' / 'Hebdomadaire' / 'Journalier'
 * Supporte aussi des strings "30 jours", "1 mois", etc.
 */
function parseDureeToDays(duree, typeContrat = '', defaultDays = 30) {
  if (duree === null || duree === undefined || duree === '') return defaultDays;
  // Cas numérique : combiné avec le type de contrat
  if (typeof duree === 'number' || /^\d+$/.test(String(duree).trim())) {
    const n = parseInt(String(duree), 10);
    const t = String(typeContrat || '').toLowerCase();
    if (t.includes('journalier') || t.includes('jour'))   return n;
    if (t.includes('hebdomad') || t.includes('semaine'))  return n * 7;
    if (t.includes('mensuel') || t.includes('mois'))      return n * 30;
    if (t.includes('trimestr'))                            return n * 90;
    if (t.includes('semestr'))                             return n * 180;
    if (t.includes('annuel') || t.includes('an'))          return n * 365;
    // Pas de type connu : si la valeur est petite (<=12), on suppose mois (défaut SESAM)
    return n <= 12 ? n * 30 : n;
  }
  // Cas string textuelle
  const s = String(duree).trim().toLowerCase();
  if (s.includes('mensuel') || s === 'mois') return 30;
  if (s.includes('trimestr')) return 90;
  if (s.includes('semestr')) return 180;
  if (s.includes('annuel') || s === 'an' || s === 'année') return 365;
  const mJ = s.match(/(\d+)\s*(jours?|j\b)/);
  if (mJ) return parseInt(mJ[1], 10);
  const mM = s.match(/(\d+)\s*(mois|m)/);
  if (mM) return parseInt(mM[1], 10) * 30;
  const mA = s.match(/(\d+)\s*(ans?|année?s?)/);
  if (mA) return parseInt(mA[1], 10) * 365;
  const mSem = s.match(/(\d+)\s*(sem|semaines?)/);
  if (mSem) return parseInt(mSem[1], 10) * 7;
  return defaultDays;
}

/**
 * Calcule la date de fin et la durée en jours effective.
 * Priorise :
 *  1. `date_fin` au niveau du contrat
 *  2. somme des `duree_jours` des lignes si présentes
 *  3. date_debut + parseDureeToDays(duree, type_contrat)
 */
function computeDateFin(contrat) {
  if (contrat?.date_fin) return new Date(contrat.date_fin);
  if (!contrat?.date_debut) return null;
  const start = new Date(contrat.date_debut);
  let days = 0;
  // 1) Lignes multi-types avec duree_jours : prendre la ligne la plus longue (fin la plus tardive)
  if (Array.isArray(contrat.lignes) && contrat.lignes.length > 0) {
    const maxJours = contrat.lignes.reduce((max, l) => {
      const j = parseInt(l?.duree_jours || 0, 10);
      return Number.isFinite(j) && j > max ? j : max;
    }, 0);
    if (maxJours > 0) days = maxJours;
  }
  if (!days) {
    days = parseDureeToDays(contrat.duree, contrat.type_contrat, 30);
  }
  const end = new Date(start);
  end.setDate(end.getDate() + days);
  return end;
}

export default function Contrats({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewContratModal, setShowNewContratModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [previewContrat, setPreviewContrat] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewPdfLoading, setPreviewPdfLoading] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [signaturesHistory, setSignaturesHistory] = useState([]);
  const [showSignaturesHistory, setShowSignaturesHistory] = useState(false);
  const [editingContrat, setEditingContrat] = useState(null);
  const [deletingContrat, setDeletingContrat] = useState(null);
  const [entreprises, setEntreprises] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [emplacementsByType, setEmplacementsByType] = useState({});
  const [sendEmail, setSendEmail] = useState('');
  
  const [newContrat, setNewContrat] = useState({
    entreprise: '',
    type_panneau: 'LED',
    nombre_affiches: 1,
    emplacement: '',
    type_contrat: 'Mensuel',
    duree: 1,
    date_debut: new Date().toISOString().split('T')[0],
    date_signature: new Date().toISOString().split('T')[0],
    representant_client_nom: '',
    representant_client_telephone: '',
    date_fin: '',
    montant: 0,
    panneau_allume: true // État du panneau
  });

  // Multi-types lignes (comme pour le devis)
  const [contratLignes, setContratLignes] = useState([]);
  const [contratMode, setContratMode] = useState('multi');

  const [showTarifModal, setShowTarifModal] = useState(false);
  const [selectedTarif, setSelectedTarif] = useState(null);
  const [showSendTarifModal, setShowSendTarifModal] = useState(false);
  const [tarifRecipient, setTarifRecipient] = useState('');
  const [prospects, setProspects] = useState([]);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showProlongModal, setShowProlongModal] = useState(false);
  const [prolongContrat, setProlongContrat] = useState(null);

  // Recherche & filtres (UI v2)
  const [searchTerm, setSearchTerm] = useState('');
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [filterStatut, setFilterStatut] = useState('');
  const [filterType, setFilterType] = useState('');

  // Panneau latéral "Détails du contrat"
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [detailContrat, setDetailContrat] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  const openDetailPanel = (contrat) => {
    setDetailContrat(contrat);
    setNotesDraft(contrat.notes || '');
    setEditingNotes(false);
    setShowDetailPanel(true);
  };

  const handleSaveNotes = async () => {
    if (!detailContrat) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${detailContrat.id}`, {
        ...detailContrat,
        notes: notesDraft,
      });
      toast.success('Note enregistrée');
      const updated = { ...detailContrat, notes: notesDraft };
      setDetailContrat(updated);
      setEditingNotes(false);
      loadContrats();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  useEffect(() => {
    loadContrats();
    loadEntreprises();
    loadEmplacements();
    loadProspects();
    fetchEmplacementsByType().then((r) => setEmplacementsByType(r.byType || {})).catch(() => {});
  }, []);

  // Pré-remplissage depuis la conversion d'un prospect en client (Prospects.js)
  useEffect(() => {
    if (location.state?.openNewModal) {
      const st = location.state;
      setNewContrat((prev) => ({
        ...prev,
        ...(st.prefillEntreprise ? { entreprise: st.prefillEntreprise } : {}),
        ...(st.prefillDebut ? { date_debut: st.prefillDebut } : {}),
        ...(st.prefillFin ? { date_fin: st.prefillFin } : {}),
        ...(st.prefillEmplacement ? { emplacement: st.prefillEmplacement } : {}),
      }));
      if (st.prefillEmplacement && st.prefillType) {
        const prix = { 'LED Digital': 5000, 'Déroulant': 3000, 'Grand Format': 4000, 'Poteau': 2500 }[st.prefillType] || 3000;
        const duree = st.prefillDebut && st.prefillFin ? Math.max(1, Math.round((new Date(st.prefillFin) - new Date(st.prefillDebut)) / 86400000)) : 30;
        setContratLignes([{
          type_panneau: st.prefillType, nombre_affiches: 1, emplacement: st.prefillEmplacement, emplacements: [st.prefillEmplacement],
          affiches_par_emplacement: { [st.prefillEmplacement]: 1 }, prix_unitaire_jour: prix, duree_jours: duree,
          date_debut: st.prefillDebut || '', date_fin: st.prefillFin || '', surface: '', dimension: '', frais_conception: 20000, frais_conception_auto: true,
        }]);
        toast.info(`Créneau libre : ${st.prefillEmplacement} du ${st.prefillDebut} au ${st.prefillFin}`, { id: 'creneau-libre' });
      }
      setShowNewContratModal(true);
      // Nettoyer le state pour éviter une réouverture au refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadContrats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contrats`);
      setContrats(response.data);
    } catch (error) {
      console.error('Error loading contrats:', error);
      toast.error('Erreur lors du chargement des contrats');
    } finally {
      setLoading(false);
    }
    // Historique signatures (silencieux)
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/signatures/history`);
      setSignaturesHistory(r.data.items || []);
    } catch (e) {
      // silencieux
    }
  };

  const loadEntreprises = async () => {
    try {
      const [prospectsRes, clientsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prospects`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/clients`)
      ]);
      const allEntreprises = [
        ...prospectsRes.data.map(p => ({
          nom: p.entreprise,
          type: 'Prospect',
          contact: p.contact || '',
          telephone: p.telephone || '',
          email: p.email || '',
        })),
        ...clientsRes.data.map(c => ({
          nom: c.entreprise,
          type: 'Client',
          contact: c.nom_contact || c.contact || '',
          telephone: c.telephone || '',
          email: c.email || '',
        })),
      ];
      setEntreprises(allEntreprises);
    } catch (error) {
      console.error('Error loading entreprises:', error);
    }
  };

  const loadEmplacements = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/emplacements`);
      setEmplacements(response.data);
    } catch (error) {
      console.error('Error loading emplacements:', error);
    }
  };

  const loadProspects = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/prospects`);
      setProspects(response.data);
    } catch (error) {
      console.error('Error loading prospects:', error);
    }
  };

  const calculateMontant = (type_panneau, nombre_affiches, type_contrat, duree) => {
    // Grille tarifaire SESAM
    const TARIFS_SESAM = {
      'LED': {
        prix_unitaire: 4000,  // Prix par image animée
        capacite: 10,          // Nombre d'images par panneau
        prix_jour: 40000       // Prix total par jour
      },
      'Déroulant': {
        prix_unitaire: 5000,   // Prix par affiche
        capacite: 12,          // Nombre d'affiches par panneau
        prix_jour: 60000       // Prix total par jour
      },
      'Poteau': {
        prix_unitaire: 2500,   // Prix par poteau
        capacite: 25,           // 25 poteaux par avenue
        prix_jour: 62500        // Prix total par jour (25 × 2500)
      }
    };

    const tarif = TARIFS_SESAM[type_panneau] || TARIFS_SESAM['Déroulant'];
    
    // Cas spécial pour Grand Format avec tarifs fixes
    if (type_panneau === 'Grand Format') {
      switch(type_contrat) {
        case 'Journalier':
          return tarif.prix_jour * duree;
        case 'Mensuel':
          return tarif.prix_mensuel * duree;
        case 'Semestriel':
          return tarif.prix_semestriel * duree;
        case 'Annuel':
          return tarif.prix_annuel * duree;
        default:
          return tarif.prix_jour * duree;
      }
    }
    
    // Calcul basé sur le nombre de jours pour LED et Déroulant
    let jours = 1;
    switch(type_contrat) {
      case 'Journalier':
        jours = duree;
        break;
      case 'Hebdomadaire':
        jours = duree * 7;
        break;
      case 'Mensuel':
        jours = duree * 30;
        break;
      default:
        jours = duree;
    }
    
    // Montant = prix par jour × nombre de jours × nombre d'affiches/images
    return tarif.prix_unitaire * nombre_affiches * jours;
  };

  const handleNewContratChange = (field, value) => {
    const updated = { ...newContrat, [field]: value };
    updated.montant = calculateMontant(
      updated.type_panneau,
      parseInt(updated.nombre_affiches) || 1,
      updated.type_contrat,
      parseInt(updated.duree) || 1
    );
    setNewContrat(updated);
  };

  const handleCreateContrat = async () => {
    if (!newContrat.entreprise) {
      toast.error("Sélectionnez une entreprise");
      return;
    }
    if (!contratLignes.length) {
      toast.error("Cochez au moins un type de panneau et complétez ses informations");
      return;
    }
    const invalid = contratLignes.find(l =>
      !l.type_panneau || !l.nombre_affiches || !l.prix_unitaire_jour || !l.duree_jours || !l.emplacement
    );
    if (invalid) {
      toast.error(`Champs manquants pour le type "${invalid.type_panneau}"`);
      return;
    }

    try {
      const payload = {
        entreprise: newContrat.entreprise,
        date_signature: newContrat.date_signature,
        date_debut: newContrat.date_debut,
        representant_client_nom: newContrat.representant_client_nom,
        representant_client_telephone: newContrat.representant_client_telephone,
        type_contrat: newContrat.type_contrat,
        duree: newContrat.duree,
        avec_tva: newContrat.avec_tva !== false,
        lignes: contratLignes.map(l => ({
          type_panneau: l.type_panneau,
          nombre_affiches: Number(l.nombre_affiches) || 0,
          emplacement: l.emplacement,
          emplacements: Array.isArray(l.emplacements) ? l.emplacements : [],
          affiches_par_emplacement: l.affiches_par_emplacement || {},
          prix_unitaire_jour: Number(l.prix_unitaire_jour) || 0,
          duree_jours: Number(l.duree_jours) || 0,
          date_debut: l.date_debut,
          date_fin: l.date_fin,
          surface: l.surface || '',
          dimension: l.dimension || '',
          frais_conception: Number(l.frais_conception) || 0,
        })),
      };
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contrats`, payload);
      const newId = res.data?.id;

      // Téléchargement automatique du PDF
      if (newId) {
        try {
          const pdfRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${newId}/pdf`,
            { responseType: 'blob' }
          );
          const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = `${res.data?.numero_contrat || 'contrat'}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        } catch (e) {
          console.error("PDF download error", e);
        }
      }

      toast.success('Contrat créé et PDF téléchargé');
      setShowNewContratModal(false);
      setNewContrat({
        entreprise: '',
        type_panneau: 'LED',
        nombre_affiches: 1,
        emplacement: '',
        type_contrat: 'Mensuel',
        duree: 1,
        date_debut: new Date().toISOString().split('T')[0],
        date_signature: new Date().toISOString().split('T')[0],
        representant_client_nom: '',
        representant_client_telephone: '',
        date_fin: '',
        montant: 0,
        panneau_allume: true,
      });
      setContratLignes([]);
      loadContrats();
    } catch (error) {
      console.error('Error creating contrat:', error);
      const msg = error?.response?.data?.detail || 'Erreur lors de la création du contrat';
      toast.error(typeof msg === 'string' ? msg : 'Erreur');
    }
  };

  const openPdfPreview = async (contrat) => {
    if (!contrat?.id) {
      toast.error("Le contrat n'a pas encore été enregistré");
      return;
    }
    setPreviewContrat(contrat);
    setPreviewPdfLoading(true);
    setShowPreviewModal(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewPdfUrl(url);
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger le PDF du contrat");
      setShowPreviewModal(false);
    } finally {
      setPreviewPdfLoading(false);
    }
  };

  const handlePreview = () => {
    toast.info("Enregistrez d'abord le contrat pour le prévisualiser");
  };

  const handleDownloadPDF = async (contrat) => {
    try {
      toast.success('Génération du PDF en cours...');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/pdf`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${contrat.numero_contrat || `Contrat_${contrat.entreprise}`}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success('PDF téléchargé');
    } catch (e) {
      console.error(e);
      toast.error("Impossible de télécharger ce contrat");
    }
  };

  const handleSendToClient = async (contrat) => {
    toast.success(`Contrat envoyé à ${contrat.entreprise} pour signature`);
  };

  const handleEdit = (contrat) => {
    setEditingContrat({...contrat});
    setShowEditModal(true);
  };

  const handleUpdateContrat = async () => {
    if (!editingContrat.entreprise || !editingContrat.emplacement) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${editingContrat.id}`, editingContrat);
      toast.success('Contrat modifié avec succès');
      setShowEditModal(false);
      setEditingContrat(null);
      loadContrats();
    } catch (error) {
      console.error('Error updating contrat:', error);
      toast.error('Erreur lors de la modification du contrat');
    }
  };

  const handleEditChange = (field, value) => {
    const updated = { ...editingContrat, [field]: value };
    if (['type_panneau', 'nombre_affiches', 'type_contrat', 'duree'].includes(field)) {
      updated.montant = calculateMontant(
        updated.type_panneau,
        parseInt(updated.nombre_affiches) || 1,
        updated.type_contrat,
        parseInt(updated.duree) || 1
      );
    }
    setEditingContrat(updated);
  };

  const handleSend = async (contrat) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/email`);
      setSendEmail(response.data.email);
      setSelectedContrat(contrat);
      setShowSendModal(true);
    } catch (error) {
      console.error('Error fetching email:', error);
      toast.error('Erreur lors de la récupération de l\'email');
    }
  };

  const handleSendConfirm = async () => {
    toast.success(`Contrat envoyé à ${sendEmail}`);
    setShowSendModal(false);
  };

  // === Signature électronique ===
  const handleSendForSignature = async (contrat) => {
    try {
      // Récupère l'email client (depuis le contrat ou via /email)
      let email = contrat.client_email || '';
      if (!email) {
        try {
          const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/email`);
          email = r.data?.email || '';
        } catch {}
      }
      const promptEmail = window.prompt(
        `Envoyer le contrat ${contrat.numero || ''} pour signature électronique.\n\nEmail du client :`,
        email
      );
      if (!promptEmail) return;
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/send-signature`,
        { email: promptEmail.trim(), nom: contrat.entreprise || '' }
      );
      const link = data?.sign_url || '';
      toast.success(
        `Email envoyé à ${promptEmail.trim()} ! Le client peut signer en ligne.`,
        {
          duration: 8000,
          description: link ? `Lien direct : ${link}` : undefined,
          action: link ? { label: 'Copier le lien', onClick: () => navigator.clipboard.writeText(link) } : undefined,
        }
      );
      loadContrats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi pour signature');
    }
  };

  const handleDownloadSigned = async (contrat) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/signed-pdf`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat-${contrat.numero || contrat.id}-signe.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Contrat signé téléchargé');
    } catch (e) {
      toast.error('Impossible de télécharger le contrat signé');
    }
  };

  const handleCopySignatureLink = async (contrat) => {
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}/generate-signature-link`
      );
      const url = r.data.sign_url;
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Lien de signature copié dans le presse-papier');
      } catch {
        // Fallback si clipboard API indisponible
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Lien de signature copié');
      }
      // Rafraîchit pour montrer le nouvel état "pending"
      loadContrats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la génération du lien');
    }
  };

  const handleDeleteClick = (contrat) => {
    setDeletingContrat(contrat);
    setShowDeleteConfirm(true);
  };

  const handlePayContrat = async (contrat) => {
    if (contrat.statut === 'Payé') {
      toast.info('Ce contrat est déjà marqué comme payé');
      return;
    }
    if (!window.confirm(`Marquer le contrat ${contrat.numero_contrat || contrat.entreprise} comme PAYÉ ?`)) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}`, { statut: 'Payé' });
      toast.success('Contrat marqué comme payé');
      loadContrats();
    } catch (error) {
      console.error('Error paying contrat:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du marquage du paiement');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${deletingContrat.id}`);
      toast.success('Contrat supprimé avec succès');
      setShowDeleteConfirm(false);
      setDeletingContrat(null);
      loadContrats();
    } catch (error) {
      console.error('Error deleting contrat:', error);
      toast.error('Erreur lors de la suppression du contrat');
    }
  };

  const handleViewDetails = (contrat) => {
    setSelectedContrat(contrat);
    setShowDetailModal(true);
  };

  // Ouverture directe d'un contrat via /contrats?contrat=ID (depuis Occupation)
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('contrat');
    if (!id || !contrats.length) return;
    const c = contrats.find((x) => x.id === id);
    if (c) { handleViewDetails(c); navigate(location.pathname, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrats, location.search]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  // Tarification offers
  const tarifOffers = [
    {
      id: 'basic',
      name: 'BASIC',
      subtitle: 'Offre de base',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      textColor: 'text-blue-700',
      price: {
        deroulant: 4000,
        led: 5000
      },
      features: [
        'Panneaux déroulants: 4 000 FCFA/jour',
        'Panneaux LED: 5 000 FCFA/jour',
        'Minimum 2 affiches par panneau',
        'Changement tous les 3 mois',
        'Contrat minimum: 3 mois'
      ],
      excluded: [
        'Conception des affiches non incluse'
      ],
      discount: 0
    },
    {
      id: 'essentiel',
      name: 'ESSENTIEL',
      subtitle: 'Offre recommandée',
      color: 'from-orange-500 to-amber-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
      textColor: 'text-orange-700',
      price: {
        deroulant: 3600,
        led: 4500
      },
      features: [
        '6 affiches par panneau',
        'Réduction de 10% sur les tarifs',
        'Panneaux déroulants: 3 600 FCFA/jour',
        'Panneaux LED: 4 500 FCFA/jour',
        'Choix des emplacements sur le panneau',
        'Contrat: 3 mois'
      ],
      excluded: [
        'Conception des affiches non incluse'
      ],
      discount: 10
    },
    {
      id: 'or',
      name: 'OR',
      subtitle: 'Offre premium',
      color: 'from-yellow-500 to-amber-500',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-400',
      textColor: 'text-yellow-800',
      price: {
        deroulant: 2800,
        led: 3500
      },
      features: [
        '12 affiches minimum (4 panneaux)',
        'Réduction de 30% sur les tarifs',
        'Panneaux déroulants: 2 800 FCFA/jour',
        'Panneaux LED: 3 500 FCFA/jour',
        'Choix du nombre d\'affiches',
        'Choix des emplacements',
        'Conception graphique incluse',
        'Support prioritaire',
        'Changements flexibles'
      ],
      excluded: [],
      discount: 30
    }
  ];

  const handleViewTarif = (tarif) => {
    setSelectedTarif(tarif);
    setShowTarifModal(true);
  };

  const handleSendTarif = () => {
    setShowTarifModal(false);
    setShowSendTarifModal(true);
  };

  const handleSendTarifEmail = async () => {
    if (!tarifRecipient) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/send-tarif`, {
        email: tarifRecipient,
        tarif: selectedTarif
      });
      toast.success(`Grille tarifaire envoyée à ${tarifRecipient}`);
      setShowSendTarifModal(false);
      setTarifRecipient('');
    } catch (error) {
      console.error('Error sending tarif:', error);
      toast.error('Erreur lors de l\'envoi de la grille tarifaire');
    }
  };

  const getStatutBadge = (statut) => {
    const colors = {
      'Actif': 'bg-green-100 text-green-700',
      'Expiré': 'bg-red-100 text-red-700',
      'En attente': 'bg-yellow-100 text-yellow-700'
    };
    return colors[statut] || 'bg-gray-100 text-gray-700';
  };

  const getPanneauBadge = (type) => {
    const colors = {
      'LED': 'bg-blue-100 text-blue-700',
      'Déroulant': 'bg-orange-100 text-orange-700',
      'Grand Format': 'bg-purple-100 text-purple-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const togglePanneauState = async (contrat, e) => {
    e.stopPropagation();
    try {
      const newState = !contrat.panneau_allume;
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/contrats/${contrat.id}`, {
        ...contrat,
        panneau_allume: newState
      });
      toast.success(newState ? 'Panneau allumé ✅' : 'Panneau éteint ⛔');
      loadContrats();
    } catch (error) {
      console.error('Error toggling panneau state:', error);
      toast.error('Erreur lors du changement d\'état');
    }
  };

  const handleProlongContract = (contrat, e) => {
    e.stopPropagation();
    setProlongContrat(contrat);
    setShowProlongModal(true);
  };

  const handlePreviewPDF = (contrat, e) => {
    if (e) e.stopPropagation();
    openPdfPreview(contrat);
  };

  const generateContractPDF = (contrat) => {
    // Générer le contenu PDF (texte simple pour l'instant)
    const pdfContent = `
==============================================
      CONTRAT PUBLICITAIRE SESAM
==============================================

Client: ${contrat.entreprise}
Type de panneau: ${contrat.type_panneau}
Nombre d'affiches: ${contrat.nombre_affiches || 1}
Emplacement: ${contrat.emplacement || 'Non spécifié'}

Durée: ${contrat.duree || contrat.duree_mois} mois
Date début: ${contrat.date_debut}
Date fin: ${contrat.date_fin}

Montant total: ${formatCurrency(contrat.montant)}
Type de contrat: ${contrat.type_contrat || 'Mensuel'}

Statut du panneau: ${contrat.panneau_allume ? 'ALLUMÉ ✅' : 'ÉTEINT ⛔'}
Statut du contrat: ${contrat.statut}

----------------------------------------------
SESAM - Service aux Sociétés Africaines en Marketing
Contact: +236 75 50 37 02
www.sesamrca.com
==============================================
    `;
    
    // Créer le téléchargement
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contrat_${contrat.entreprise}_${contrat.id}.txt`;
    link.click();
    
    toast.success('Contrat téléchargé');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="contrats">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="contrats">
      <div className="w-full px-4 sm:px-6 py-4 space-y-6" data-testid="contrats-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1C2A46]">Contrats</h1>
            <p className="text-sm text-slate-500 mt-1">
              Gérez et suivez tous vos contrats en cours et expirés.
            </p>
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher un contrat, une société..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-10 bg-white"
                data-testid="contrats-search-input"
              />
            </div>
            <Button
              variant="outline"
              className="gap-2 h-10"
              onClick={() => setShowFiltersPanel((v) => !v)}
              data-testid="contrats-filters-btn"
            >
              <Filter className="h-4 w-4" />
              Filtres
            </Button>
            <Button
              className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowNewContratModal(true)}
              data-testid="contrats-add-btn"
            >
              <Plus className="h-4 w-4" />
              Nouveau contrat
            </Button>
          </div>
        </div>

        {/* Filtres extensibles */}
        {showFiltersPanel && (
          <Card className="border-0 shadow-sm" data-testid="contrats-filters-panel">
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div>
                <Label className="text-xs">Statut</Label>
                <select
                  value={filterStatut}
                  onChange={(e) => setFilterStatut(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white block mt-1 min-w-[180px]"
                >
                  <option value="">Tous</option>
                  <option>Actif</option>
                  <option>À venir</option>
                  <option>Expiré</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Type de panneau</Label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="h-9 px-3 rounded-md border border-slate-200 text-sm bg-white block mt-1 min-w-[180px]"
                >
                  <option value="">Tous</option>
                  <option>LED</option>
                  <option>Déroulant</option>
                  <option>Grand Format</option>
                  <option>Intérieur</option>
                </select>
              </div>
              {(filterStatut || filterType) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 mt-5"
                  onClick={() => {
                    setFilterStatut('');
                    setFilterType('');
                  }}
                >
                  Réinitialiser
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* KPI cards */}
        {(() => {
          const today = new Date();
          const in30 = new Date();
          in30.setDate(today.getDate() + 30);
          const total = contrats.length;
          let actifs = 0;
          let aVenir = 0;
          let expires = 0;
          contrats.forEach((c) => {
            const dDeb = c.date_debut ? new Date(c.date_debut) : null;
            const dFin = c.date_fin ? new Date(c.date_fin) : null;
            if (dDeb && dDeb > today && dDeb <= in30) aVenir += 1;
            if (dFin && dFin < today) expires += 1;
            else if (dDeb && dFin && dDeb <= today && dFin >= today) actifs += 1;
          });
          const filtered = contrats.filter((c) => {
            const q = searchTerm.toLowerCase();
            const matchSearch =
              !q ||
              c.entreprise?.toLowerCase().includes(q) ||
              c.emplacement?.toLowerCase().includes(q) ||
              c.type_panneau?.toLowerCase().includes(q);
            const dDeb = c.date_debut ? new Date(c.date_debut) : null;
            const dFin = c.date_fin ? new Date(c.date_fin) : null;
            const computedStatut =
              dFin && dFin < today ? 'Expiré'
              : dDeb && dDeb > today ? 'À venir'
              : 'Actif';
            const matchStatut = !filterStatut || computedStatut === filterStatut;
            const matchType = !filterType || (c.type_panneau || '').toLowerCase().includes(filterType.toLowerCase());
            return matchSearch && matchStatut && matchType;
          });

          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="contrats-kpis">
                <KpiContrat
                  iconBg="bg-blue-100 text-blue-600"
                  Icon={FileBarChart}
                  label="Total contrats"
                  value={total}
                  sub="Tous les contrats"
                  testid="kpi-total-contrats"
                />
                <KpiContrat
                  iconBg="bg-emerald-100 text-emerald-600"
                  Icon={FileText}
                  label="Contrats actifs"
                  value={actifs}
                  sub="En cours"
                  testid="kpi-contrats-actifs"
                />
                <KpiContrat
                  iconBg="bg-amber-100 text-amber-600"
                  Icon={Hourglass}
                  label="À venir"
                  value={aVenir}
                  sub="Prochains 30 jours"
                  testid="kpi-contrats-avenir"
                />
                <KpiContrat
                  iconBg="bg-rose-100 text-rose-600"
                  Icon={CalendarX}
                  label="Expirés"
                  value={expires}
                  sub="Voir les contrats"
                  testid="kpi-contrats-expires"
                />
              </div>

              {/* Historique des signatures reçues (collapsible) */}
              <Card className="border-0 shadow-sm" data-testid="signatures-history-panel">
                <CardContent className="p-4">
                  <button
                    type="button"
                    onClick={() => setShowSignaturesHistory((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 group"
                    data-testid="btn-toggle-signatures-history"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-bold text-slate-900">Historique des signatures reçues</div>
                        <div className="text-xs text-slate-500">
                          {signaturesHistory.length} signature{signaturesHistory.length > 1 ? 's' : ''} enregistrée{signaturesHistory.length > 1 ? 's' : ''}
                          {signaturesHistory.length > 0 && (
                            <> · dernière le {new Date(signaturesHistory[0].signed_at).toLocaleDateString('fr-FR')}</>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                        {signaturesHistory.length}
                      </span>
                      {showSignaturesHistory ? <X className="h-4 w-4 text-slate-500" /> : <Eye className="h-4 w-4 text-slate-500" />}
                    </div>
                  </button>

                  {showSignaturesHistory && (
                    <div className="mt-4 border-t pt-4 space-y-2 max-h-80 overflow-y-auto" data-testid="signatures-history-list">
                      {signaturesHistory.length === 0 ? (
                        <div className="text-center py-6 text-sm text-slate-400 italic">
                          Aucune signature reçue pour le moment.
                        </div>
                      ) : (
                        signaturesHistory.map((s) => (
                          <div key={s.id || s.token} className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                <CheckCircle className="h-4 w-4" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-900 truncate">
                                  {s.entreprise || s.client_name || 'Client'}
                                </div>
                                <div className="text-xs text-slate-500 truncate">
                                  Contrat n° <span className="font-mono">{s.contrat_numero || '—'}</span>
                                  {s.signer_name && <> · signé par {s.signer_name}</>}
                                </div>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs font-semibold text-slate-700 whitespace-nowrap">
                                {s.signed_at ? new Date(s.signed_at).toLocaleDateString('fr-FR') : '—'}
                              </div>
                              <div className="text-[10px] text-slate-500 whitespace-nowrap">
                                {s.signed_at ? new Date(s.signed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
                              onClick={async () => {
                                const c = contrats.find((c) => c.id === s.contrat_id);
                                if (c) {
                                  await handleDownloadSigned(c);
                                } else {
                                  await handleDownloadSigned({ id: s.contrat_id, numero: s.contrat_numero });
                                }
                              }}
                              data-testid={`history-download-${s.id || s.token}`}
                            >
                              <Download className="h-3 w-3 mr-1" /> PDF
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Liste des contrats — 2 colonnes compactes */}
              <div className="grid grid-cols-1 gap-4" data-testid="contrats-list">
                {filtered.length === 0 ? (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-12 text-center text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      Aucun contrat trouvé
                    </CardContent>
                  </Card>
                ) : (
                  filtered.map((contrat, index) => (
                    <ContratCard
                      key={contrat.id}
                      contrat={contrat}
                      index={index}
                      formatCurrency={formatCurrency}
                      onOpenDetail={() => openDetailPanel(contrat)}
                      onPreview={(e) => handlePreviewPDF(contrat, e)}
                      onDownload={(e) => { if (e) e.stopPropagation(); handleDownloadPDF(contrat); }}
                      onProlong={(e) => handleProlongContract(contrat, e)}
                      onEdit={() => handleEdit(contrat)}
                      onSend={() => handleSend(contrat)}
                      onSendForSignature={() => handleSendForSignature(contrat)}
                      onCopySignatureLink={() => handleCopySignatureLink(contrat)}
                      onDownloadSigned={() => handleDownloadSigned(contrat)}
                      onDelete={() => handleDeleteClick(contrat)}
                      onPay={() => handlePayContrat(contrat)}
                    />
                  ))
                )}
              </div>

              {filtered.length > 6 && (
                <div className="text-center text-sm text-slate-500 pt-2 pb-4 flex items-center justify-center gap-2">
                  <Download className="h-4 w-4 rotate-90" />
                  Faites défiler vers le bas pour voir plus de contrats
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Sheet Nouveau Contrat */}
      <Sheet open={showNewContratModal} onOpenChange={setShowNewContratModal}>
        <SheetContent side="right" className="w-full sm:max-w-3xl p-6 overflow-y-auto" data-testid="new-contrat-sheet">
          <SheetHeader>
            <SheetTitle>Nouveau Contrat Publicitaire</SheetTitle>
            <SheetDescription>
              Remplissez les informations du contrat pour le client
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-4 mt-4">
            {/* Section: Entreprise + représentant client (auto-fill) */}
            <div className="space-y-3">
              <div>
                <Label>Entreprise / Client *</Label>
                <Select
                  value={newContrat.entreprise}
                  onValueChange={(val) => {
                    const ent = entreprises.find((e) => e.nom === val);
                    setNewContrat((prev) => ({
                      ...prev,
                      entreprise: val,
                      representant_client_nom: prev.representant_client_nom || ent?.contact || '',
                      representant_client_telephone: prev.representant_client_telephone || ent?.telephone || '',
                    }));
                  }}
                >
                  <SelectTrigger data-testid="contrat-entreprise-select">
                    <SelectValue placeholder="Sélectionner une entreprise" />
                  </SelectTrigger>
                  <SelectContent>
                    {entreprises.map((ent, idx) => (
                      <SelectItem key={idx} value={ent.nom}>
                        {ent.nom} ({ent.type})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Les informations (nom du contact, téléphone) sont pré-remplies automatiquement.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nom du représentant client *</Label>
                  <Input
                    value={newContrat.representant_client_nom}
                    onChange={(e) => handleNewContratChange('representant_client_nom', e.target.value)}
                    placeholder="Ex: TARIBOU WESTER"
                    data-testid="contrat-rep-nom"
                  />
                </div>
                <div>
                  <Label>Téléphone client *</Label>
                  <Input
                    value={newContrat.representant_client_telephone}
                    onChange={(e) => handleNewContratChange('representant_client_telephone', e.target.value)}
                    placeholder="+236 ..."
                    data-testid="contrat-rep-tel"
                  />
                </div>
                <div>
                  <Label>Date de signature *</Label>
                  <Input
                    type="date"
                    value={newContrat.date_signature}
                    onChange={(e) => handleNewContratChange('date_signature', e.target.value)}
                    data-testid="contrat-date-signature"
                  />
                </div>
                <div>
                  <Label>Date de début (campagne)</Label>
                  <Input
                    type="date"
                    value={newContrat.date_debut}
                    onChange={(e) => handleNewContratChange('date_debut', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Multi-types panneaux (avec surface/dimension pour le contrat) */}
            <DevisLignesMulti
              lignes={contratLignes}
              setLignes={setContratLignes}
              mode={contratMode}
              setMode={setContratMode}
              showContractFields={true}
              emplacementsByType={emplacementsByType}
              emplacementsOptions={emplacements.map(e => ({
                value: e.nom,
                label: e.commune ? `${e.nom} — ${e.commune}` : e.nom,
              }))}
            />

            <ContratTvaToggle
              checked={newContrat.avec_tva !== false}
              onChange={(v) => handleNewContratChange('avec_tva', v)}
              totalHt={contratLignes.reduce((s, l) => s + (Number(l.prix_unitaire_jour) || 0) * (Number(l.duree_jours) || 0) * (Object.values(l.affiches_par_emplacement || {}).reduce((a, b) => a + (Number(b) || 0), 0) || Number(l.nombre_affiches) || 0), 0)}
              format={formatCurrency}
            />

            <div className="flex justify-between gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowNewContratModal(false)}>
                Annuler
              </Button>
              <Button
                onClick={handleCreateContrat}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="create-contrat-btn"
              >
                <Plus className="h-4 w-4" />
                Créer & Télécharger le Contrat
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal Preview PDF */}
      {/* Modal Prévisualisation PDF AMÉLIORÉ */}
      <Dialog open={showPreviewModal} onOpenChange={(open) => {
        setShowPreviewModal(open);
        if (!open && previewPdfUrl) {
          URL.revokeObjectURL(previewPdfUrl);
          setPreviewPdfUrl(null);
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="text-xl">Prévisualisation du Contrat (PDF officiel SESAM)</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden bg-slate-100">
            {previewPdfLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
              </div>
            ) : previewPdfUrl ? (
              <iframe
                title="Contrat PDF"
                src={previewPdfUrl}
                className="w-full h-full border-0"
                data-testid="contrat-pdf-iframe"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500">
                Aucun contrat à afficher.
              </div>
            )}
          </div>

          <div className="flex justify-between gap-2 px-6 py-3 border-t bg-white">
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Fermer
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => previewContrat && handleDownloadPDF(previewContrat)}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="preview-download-btn"
              >
                <Download className="h-4 w-4" />
                Télécharger PDF
              </Button>
              <Button
                onClick={() => {
                  if (previewContrat) handleSend(previewContrat);
                  setShowPreviewModal(false);
                }}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                <Send className="h-4 w-4" />
                Envoyer au Client
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Détails Contrat */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              Détails du Contrat
            </DialogTitle>
          </DialogHeader>
          
          {selectedContrat && (
            <div className="space-y-6 mt-4">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border-2 border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedContrat.entreprise}</h3>
                <Badge className={`${getStatutBadge(selectedContrat.statut)} text-base px-3 py-1`}>
                  {selectedContrat.statut}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Type de Panneau</div>
                  <Badge className={`${getPanneauBadge(selectedContrat.type_panneau)} text-lg px-3 py-1`}>
                    {selectedContrat.type_panneau}
                  </Badge>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Durée du Contrat</div>
                  <div className="text-xl font-bold text-gray-900">{selectedContrat.duree || selectedContrat.duree_mois} mois</div>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Date de Début</div>
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-blue-600" />
                    {selectedContrat.date_debut ? selectedContrat.date_debut.split('T')[0] : 'Non spécifié'}
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Date de Fin</div>
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-red-600" />
                    {selectedContrat.date_fin ? selectedContrat.date_fin.split('T')[0] : 'À calculer'}
                  </div>
                </div>

                <div className="col-span-2 bg-green-50 p-5 rounded-lg border-2 border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Montant Total</div>
                  <div className="text-3xl font-bold text-green-600">{formatCurrency(selectedContrat.montant)}</div>
                </div>

                <div className="col-span-2 bg-blue-50 p-5 rounded-lg border-2 border-blue-200">
                  <div className="text-sm text-gray-600 mb-2 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Emplacement du Panneau
                  </div>
                  <div className="text-xl font-bold text-blue-900">{selectedContrat.emplacement || 'Non spécifié'}</div>
                </div>

                {/* Détail des lignes avec nombre d'affiches par emplacement */}
                {Array.isArray(selectedContrat.lignes) && selectedContrat.lignes.length > 0 && (
                  <div className="col-span-2 bg-white p-4 rounded-lg border-2 border-indigo-200" data-testid="contrat-detail-lignes">
                    <div className="text-sm font-semibold text-indigo-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Détail des panneaux et affiches
                    </div>
                    <div className="space-y-3">
                      {selectedContrat.lignes.map((l, i) => {
                        const emps = Array.isArray(l.emplacements) ? l.emplacements : [];
                        const aff = l.affiches_par_emplacement || {};
                        const totalAff = emps.length > 0
                          ? emps.reduce((s, e) => s + (parseInt(aff[e] || 0, 10) || 0), 0)
                          : (parseInt(l.nombre_affiches, 10) || 0);
                        return (
                          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300">
                                {l.type_panneau || 'Panneau'}
                              </span>
                              <span className="text-xs font-semibold text-indigo-700">
                                Total : {totalAff} affiche{totalAff > 1 ? 's' : ''}
                              </span>
                            </div>
                            {emps.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {emps.map((e) => (
                                  <div key={e} className="flex items-center justify-between bg-white px-3 py-1.5 rounded border border-slate-200 text-sm">
                                    <span className="text-slate-800 truncate mr-2" title={e}>{e}</span>
                                    <span className="font-bold text-indigo-700 whitespace-nowrap">
                                      {parseInt(aff[e] || 0, 10) || 0} / 10
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-slate-600">
                                {l.emplacement || 'Emplacement non spécifié'} — {l.nombre_affiches || 0} affiche(s)
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Responsable</div>
                  <div className="text-lg font-semibold text-gray-900">{selectedContrat.responsable}</div>
                </div>

                <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">Nombre d'Affiches</div>
                  <div className="text-lg font-semibold text-gray-900">{selectedContrat.nombre_affiches || 1}</div>
                </div>
              </div>

              {/* ═══ SIGNATURE ÉLECTRONIQUE ═══ */}
              <div className="col-span-2 mt-2 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-5" data-testid="signature-block">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-md">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-indigo-900">Signature électronique</h4>
                    <p className="text-xs text-indigo-700">Lien à envoyer au client pour signer en ligne</p>
                  </div>
                </div>

                {selectedContrat.signature_status === 'signed' ? (
                  <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-bold text-emerald-800">Contrat signé</div>
                      <div className="text-sm text-emerald-700 mt-1">
                        Signé le{' '}
                        <span className="font-semibold">
                          {selectedContrat.signature_signed_at
                            ? new Date(selectedContrat.signature_signed_at).toLocaleString('fr-FR', {
                                day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                              })
                            : '—'}
                        </span>
                        {selectedContrat.signature_signer_name && (
                          <> · par <span className="font-semibold">{selectedContrat.signature_signer_name}</span></>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={async () => {
                          try {
                            const r = await axios.post(
                              `${process.env.REACT_APP_BACKEND_URL}/api/contrats/${selectedContrat.id}/generate-signature-link`
                            );
                            const url = r.data.sign_url;
                            // Copie dans le presse-papier
                            try {
                              await navigator.clipboard.writeText(url);
                              toast.success('Lien de signature copié !');
                            } catch {
                              // Fallback: sélection dans un textarea
                              const ta = document.createElement('textarea');
                              ta.value = url;
                              document.body.appendChild(ta);
                              ta.select();
                              document.execCommand('copy');
                              document.body.removeChild(ta);
                              toast.success('Lien copié');
                            }
                            // Met à jour l'affichage local
                            setSelectedContrat({ ...selectedContrat, _signature_url: url, signature_status: 'pending' });
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Erreur lors de la génération du lien');
                          }
                        }}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        data-testid="btn-copy-signature-link"
                      >
                        <Copy className="h-4 w-4 mr-1.5" /> Copier le lien de signature
                      </Button>
                      <Button
                        variant="outline"
                        className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => handleSendForSignature(selectedContrat)}
                        data-testid="btn-send-signature-email"
                      >
                        <Send className="h-4 w-4 mr-1.5" /> Envoyer par email
                      </Button>
                    </div>
                    {selectedContrat._signature_url && (
                      <div className="bg-white border border-indigo-200 rounded-md p-2.5 flex items-center gap-2">
                        <span className="text-xs text-slate-500 shrink-0">Lien :</span>
                        <input
                          readOnly
                          value={selectedContrat._signature_url}
                          className="flex-1 text-xs font-mono text-indigo-700 bg-transparent outline-none"
                          onClick={(e) => e.target.select()}
                        />
                      </div>
                    )}
                    {selectedContrat.signature_status === 'pending' && (
                      <div className="text-xs text-indigo-700 bg-indigo-100/60 border border-indigo-200 rounded p-2">
                        ⓘ En attente de signature. Le lien est valable 30 jours et donne accès à une page publique de signature.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sheet Edit Contrat */}
      <Sheet open={showEditModal} onOpenChange={setShowEditModal}>
        <SheetContent side="right" className="w-full sm:max-w-3xl p-6 overflow-y-auto" data-testid="edit-contrat-sheet">
          <SheetHeader>
            <SheetTitle>Modifier le Contrat</SheetTitle>
          </SheetHeader>
          
          {editingContrat && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Entreprise / Client *</Label>
                  <Select value={editingContrat.entreprise} onValueChange={(val) => handleEditChange('entreprise', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {entreprises.map((ent, idx) => (
                        <SelectItem key={idx} value={ent.nom}>
                          {ent.nom} ({ent.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Type de Panneau *</Label>
                  <Select value={editingContrat.type_panneau} onValueChange={(val) => handleEditChange('type_panneau', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LED">Panneau LED</SelectItem>
                      <SelectItem value="Déroulant">Panneau Déroulant</SelectItem>
                      <SelectItem value="Grand Format">Panneau Grand Format</SelectItem>
                      <SelectItem value="Poteau">Panneau Poteau</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Nombre d'Affiches *</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={editingContrat.nombre_affiches}
                    onChange={(e) => handleEditChange('nombre_affiches', e.target.value)}
                  />
                </div>

                <div className="col-span-2">
                  <Label>Emplacement *</Label>
                  <Select value={editingContrat.emplacement} onValueChange={(val) => handleEditChange('emplacement', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emplacements.map((emp) => (
                        <SelectItem key={emp.id} value={emp.nom}>
                          {emp.nom} - {emp.commune}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Type de Contrat *</Label>
                  <Select value={editingContrat.type_contrat} onValueChange={(val) => handleEditChange('type_contrat', val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mensuel">Mensuel</SelectItem>
                      <SelectItem value="Trimestriel">Trimestriel (3 mois)</SelectItem>
                      <SelectItem value="Annuel">Annuel (12 mois)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Durée *</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={editingContrat.duree}
                    onChange={(e) => handleEditChange('duree', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Date de Début *</Label>
                  <Input 
                    type="date"
                    value={editingContrat.date_debut}
                    onChange={(e) => handleEditChange('date_debut', e.target.value)}
                  />
                </div>

                <div>
                  <Label>Montant Total (Calculé)</Label>
                  <div className="mt-2 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(editingContrat.montant)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleUpdateContrat}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Enregistrer les Modifications
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Modal Send Email */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-green-600" />
              Envoyer le Contrat par Email
            </DialogTitle>
          </DialogHeader>
          
          {selectedContrat && (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Contrat à envoyer</h4>
                <p className="text-gray-700"><strong>Entreprise:</strong> {selectedContrat.entreprise}</p>
                <p className="text-gray-700"><strong>Montant:</strong> {formatCurrency(selectedContrat.montant)}</p>
                <p className="text-gray-700"><strong>Type:</strong> {selectedContrat.type_panneau}</p>
              </div>

              <div>
                <Label>Email du destinataire *</Label>
                <Input 
                  type="email"
                  value={sendEmail}
                  onChange={(e) => setSendEmail(e.target.value)}
                  placeholder="email@exemple.com"
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">Vous pouvez modifier l'adresse email si nécessaire</p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  ℹ️ <strong>Note:</strong> Le contrat sera envoyé au format PDF à l'adresse email spécifiée.
                </p>
              </div>

              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowSendModal(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleSendConfirm}
                  className="bg-green-600 hover:bg-green-700 gap-2"
                >
                  <Send className="h-4 w-4" />
                  Envoyer le Contrat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-6 w-6" />
              Confirmer la Suppression
            </DialogTitle>
          </DialogHeader>
          
          {deletingContrat && (
            <div className="space-y-4 mt-4">
              <div className="bg-red-50 p-4 rounded-lg border-2 border-red-200">
                <p className="text-gray-800 mb-2">
                  Êtes-vous sûr de vouloir supprimer ce contrat ?
                </p>
                <p className="font-bold text-gray-900">{deletingContrat.entreprise}</p>
                <p className="text-sm text-gray-600 mt-1">Montant: {formatCurrency(deletingContrat.montant)}</p>
              </div>

              <p className="text-sm text-red-600">
                ⚠️ Cette action est irréversible et supprimera définitivement le contrat.
              </p>

              <div className="flex justify-between gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleDeleteConfirm}
                  variant="destructive"
                  className="gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer Définitivement
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Tarif Details */}
      <Dialog open={showTarifModal} onOpenChange={setShowTarifModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedTarif && (
                <>
                  <div className={`px-4 py-2 rounded-lg bg-gradient-to-r ${selectedTarif.color} text-white font-bold text-2xl`}>
                    {selectedTarif.name}
                  </div>
                  <Badge className="bg-purple-500 text-white">{selectedTarif.subtitle}</Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedTarif && (
            <div className="space-y-6 mt-4">
              {/* Discount Badge */}
              {selectedTarif.discount > 0 && (
                <div className="text-center">
                  <Badge className="bg-red-500 text-white text-2xl px-6 py-2">
                    🎉 RÉDUCTION DE {selectedTarif.discount}% 🎉
                  </Badge>
                </div>
              )}

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className={`${selectedTarif.bgColor} border-2 ${selectedTarif.borderColor} rounded-xl p-6`}>
                  <h4 className="font-bold text-gray-800 mb-3">📱 Panneaux Déroulants</h4>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-blue-600">{formatCurrency(selectedTarif.price.deroulant)}</p>
                    <p className="text-sm text-gray-600 mt-1">par jour</p>
                  </div>
                </div>
                <div className={`${selectedTarif.bgColor} border-2 ${selectedTarif.borderColor} rounded-xl p-6`}>
                  <h4 className="font-bold text-gray-800 mb-3">💡 Panneaux LED</h4>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-purple-600">{formatCurrency(selectedTarif.price.led)}</p>
                    <p className="text-sm text-gray-600 mt-1">par jour</p>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Avantages inclus
                </h4>
                <div className="space-y-2">
                  {selectedTarif.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <span className="text-green-600 text-xl">✓</span>
                      <span className="text-gray-800 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Excluded */}
              {selectedTarif.excluded.length > 0 && (
                <div>
                  <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                    <span className="text-red-600">✗</span>
                    Non inclus
                  </h4>
                  <div className="space-y-2">
                    {selectedTarif.excluded.map((excluded, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <span className="text-red-600 text-xl">✗</span>
                        <span className="text-gray-800">{excluded}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="flex justify-between gap-4 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTarifModal(false)}>
                  Fermer
                </Button>
                <Button 
                  onClick={handleSendTarif}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 hover:opacity-90 text-white gap-2"
                >
                  <Send className="h-4 w-4" />
                  Envoyer cette offre par email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Grille Tarifaire Complète */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <FileText className="h-8 w-8 text-orange-600" />
              Grille Tarifaire Complète SESAM
            </DialogTitle>
            <DialogDescription>
              Consultez nos offres commerciales et tarifs détaillés pour préparer votre offre client
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Section Offres Commerciales */}
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1">
                  Offres Commerciales
                </Badge>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {tarifOffers.map((offer) => (
                  <div
                    key={offer.id}
                    onClick={() => handleViewTarif(offer)}
                    className={`cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl ${offer.bgColor} border-2 ${offer.borderColor} rounded-xl p-6`}
                  >
                    {/* Header avec gradient */}
                    <div className={`bg-gradient-to-r ${offer.color} text-white p-4 rounded-lg mb-4 text-center`}>
                      <h3 className="text-2xl font-bold">{offer.name}</h3>
                      <p className="text-sm opacity-90">{offer.subtitle}</p>
                    </div>

                    {/* Price badge */}
                    {offer.discount > 0 && (
                      <div className="text-center mb-4">
                        <Badge className="bg-red-500 text-white text-lg px-4 py-1">
                          -{offer.discount}% 🎉
                        </Badge>
                      </div>
                    )}

                    {/* Features list */}
                    <div className="space-y-2 mb-4">
                      {offer.features.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">✓</span>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Excluded features */}
                    {offer.excluded.length > 0 && (
                      <div className="space-y-2 mb-4 border-t pt-3">
                        {offer.excluded.map((excluded, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <span className="text-red-500 mt-1">✗</span>
                            <span className="text-xs text-gray-600">{excluded}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* CTA Button */}
                    <Button 
                      className={`w-full bg-gradient-to-r ${offer.color} hover:opacity-90 text-white font-semibold`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewTarif(offer);
                      }}
                    >
                      Voir les détails
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Section Grille Tarifaire Technique */}
            <div className="border-t-2 pt-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-1">
                  Tarifs Techniques
                </Badge>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Tarif LED */}
                <div className="bg-white rounded-xl p-5 shadow-md border-2 border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <div className="w-6 h-6 bg-blue-500 rounded"></div>
                    </div>
                    <div>
                      <p className="font-bold text-lg text-gray-800">Panneaux LED Digital</p>
                      <p className="text-sm text-gray-500">Écrans haute résolution</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-blue-100">
                      <span className="text-sm text-gray-600">Prix par image animée</span>
                      <span className="font-bold text-blue-600 text-lg">4 000 FCFA</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-blue-100">
                      <span className="text-sm text-gray-600">Capacité par panneau</span>
                      <span className="font-semibold text-gray-800">10 images</span>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Prix total / jour</span>
                        <span className="font-bold text-2xl text-blue-600">40 000 FCFA</span>
                      </div>
                    </div>
                    <div className="bg-blue-100 rounded-lg p-3 border-2 border-blue-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Prix total / mois</span>
                        <span className="font-bold text-2xl text-blue-700">200 000 FCFA</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">30 jours</p>
                    </div>
                  </div>
                </div>

                {/* Tarif Déroulant */}
                <div className="bg-white rounded-xl p-5 shadow-md border-2 border-orange-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                      <div className="w-6 h-6 bg-orange-500 rounded"></div>
                    </div>
                    <div>
                      <p className="font-bold text-lg text-gray-800">Panneaux Déroulants</p>
                      <p className="text-sm text-gray-500">Affiches imprimées</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-2 border-b border-orange-100">
                      <span className="text-sm text-gray-600">Prix par affiche</span>
                      <span className="font-bold text-orange-600 text-lg">5 000 FCFA</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-orange-100">
                      <span className="text-sm text-gray-600">Capacité par panneau</span>
                      <span className="font-semibold text-gray-800">12 affiches</span>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Prix total / jour</span>
                        <span className="font-bold text-2xl text-orange-600">60 000 FCFA</span>
                      </div>
                    </div>
                    <div className="bg-orange-100 rounded-lg p-3 border-2 border-orange-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Prix total / mois</span>
                        <span className="font-bold text-2xl text-orange-700">300 000 FCFA</span>
                      </div>
                      <p className="text-xs text-orange-600 mt-1">30 jours</p>
                    </div>
                  </div>
                </div>

                {/* Tarif Grand Format */}
                <div className="bg-white rounded-xl p-5 shadow-md border-2 border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                      <div className="w-6 h-6 bg-purple-500 rounded"></div>
                    </div>
                    <div>
                      <p className="font-bold text-lg text-gray-800">Panneau Grand Format</p>
                      <p className="text-sm text-gray-500">Format XXL Premium</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 font-medium">Par jour</span>
                        <span className="font-bold text-lg text-purple-600">500 000 FCFA</span>
                      </div>
                    </div>
                    <div className="bg-purple-100 rounded-lg p-3 border-2 border-purple-300">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Par mois</span>
                        <span className="font-bold text-xl text-purple-700">15 M FCFA</span>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 font-medium">Semestriel (6 mois)</span>
                        <span className="font-bold text-lg text-purple-600">90 M FCFA</span>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700 font-medium">Annuel (12 mois)</span>
                        <span className="font-bold text-lg text-purple-600">180 M FCFA</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-4 bg-purple-100 rounded-lg border border-purple-300">
                <p className="text-sm text-purple-800">
                  <strong>ℹ️ Note :</strong> Les tarifs sont calculés automatiquement lors de la création d'un contrat. 
                  Le montant total dépend du type de panneau, du nombre d'affiches/images et de la durée.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t mt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowPricingModal(false)}
              className="px-6"
            >
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Send Tarif Email */}
      <Dialog open={showSendTarifModal} onOpenChange={setShowSendTarifModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-6 w-6 text-blue-600" />
              Envoyer la Grille Tarifaire
            </DialogTitle>
            <DialogDescription>
              Sélectionnez un prospect pour recevoir l'offre {selectedTarif?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Tarif Summary */}
            {selectedTarif && (
              <div className={`${selectedTarif.bgColor} border-2 ${selectedTarif.borderColor} rounded-lg p-4`}>
                <h4 className="font-bold text-gray-900 mb-2">
                  Offre sélectionnée: {selectedTarif.name}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Panneaux déroulants:</span>
                    <span className="font-bold ml-2">{formatCurrency(selectedTarif.price.deroulant)}/jour</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Panneaux LED:</span>
                    <span className="font-bold ml-2">{formatCurrency(selectedTarif.price.led)}/jour</span>
                  </div>
                </div>
                {selectedTarif.discount > 0 && (
                  <Badge className="bg-red-500 text-white mt-2">
                    Réduction: -{selectedTarif.discount}%
                  </Badge>
                )}
              </div>
            )}

            {/* Recipient Selection */}
            <div>
              <Label>Sélectionner un prospect *</Label>
              <Select value={tarifRecipient} onValueChange={setTarifRecipient}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Choisir un prospect" />
                </SelectTrigger>
                <SelectContent>
                  {prospects.map((prospect) => (
                    <SelectItem key={prospect.id} value={prospect.email}>
                      {prospect.entreprise} ({prospect.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-2">
                💡 Le prospect recevra un email professionnel avec la grille tarifaire détaillée
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSendTarifModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSendTarifEmail}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:opacity-90 text-white gap-2"
                disabled={!tarifRecipient}
              >
                <Send className="h-4 w-4" />
                Envoyer la grille tarifaire
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Prolonger Contrat */}
      <Dialog open={showProlongModal} onOpenChange={setShowProlongModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prolonger le Contrat</DialogTitle>
            <DialogDescription>
              {prolongContrat && `Prolongation du contrat de ${prolongContrat.entreprise}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Durée supplémentaire (mois)</Label>
              <Input
                type="number"
                min="1"
                defaultValue="3"
                placeholder="Nombre de mois"
              />
            </div>
            <div>
              <Label>Nouvelle date de fin</Label>
              <Input type="date" />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowProlongModal(false)}>
                Annuler
              </Button>
              <Button 
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  toast.success('Contrat prolongé avec succès');
                  setShowProlongModal(false);
                  loadContrats();
                }}
              >
                Prolonger
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Panneau latéral "Détails du contrat" */}
      <ContratDetailPanel
        open={showDetailPanel}
        onClose={() => setShowDetailPanel(false)}
        contrat={detailContrat}
        formatCurrency={formatCurrency}
        editingNotes={editingNotes}
        notesDraft={notesDraft}
        setEditingNotes={setEditingNotes}
        setNotesDraft={setNotesDraft}
        onSaveNotes={handleSaveNotes}
        onModifyContract={() => {
          setShowDetailPanel(false);
          if (detailContrat) handleEdit(detailContrat);
        }}
        onRefaireContrat={() => {
          if (!detailContrat) return;
          setShowDetailPanel(false);
          setNewContrat({
            entreprise: detailContrat.entreprise || '',
            type_panneau: detailContrat.type_panneau || 'LED',
            nombre_affiches: detailContrat.nombre_affiches || 1,
            emplacement: detailContrat.emplacement || '',
            type_contrat: detailContrat.type_contrat || 'Mensuel',
            duree: detailContrat.duree || detailContrat.duree_mois || 1,
            date_debut: new Date().toISOString().split('T')[0],
            date_fin: '',
            montant: detailContrat.montant || 0,
            panneau_allume: true,
          });
          setShowNewContratModal(true);
        }}
        onProlongerContrat={() => {
          if (!detailContrat) return;
          setShowDetailPanel(false);
          // Point de départ : jour suivant la fin du contrat en cours (ou aujourd'hui si expiré)
          let nextStart = new Date();
          if (detailContrat.date_fin) {
            const oldEnd = new Date(detailContrat.date_fin);
            if (oldEnd >= new Date()) {
              nextStart = new Date(oldEnd);
              nextStart.setDate(nextStart.getDate() + 1);
            }
          }
          setNewContrat({
            entreprise: detailContrat.entreprise || '',
            type_panneau: detailContrat.type_panneau || 'LED',
            nombre_affiches: detailContrat.nombre_affiches || 1,
            emplacement: detailContrat.emplacement || '',
            type_contrat: detailContrat.type_contrat || 'Mensuel',
            duree: detailContrat.duree || detailContrat.duree_mois || 1,
            date_debut: nextStart.toISOString().split('T')[0],
            date_fin: '',
            montant: detailContrat.montant || 0,
            panneau_allume: true,
            _prolongation_origine_id: detailContrat.id || '',
            _prolongation_origine_numero: detailContrat.numero_contrat || detailContrat.numero || '',
          });
          setShowNewContratModal(true);
          toast.info(`Prolongation du contrat ${detailContrat.numero_contrat || detailContrat.numero || ''} — vérifiez les infos puis validez`);
        }}
      />
    </Layout>
  );
}

function KpiContrat({ iconBg, Icon, label, value, sub, testid }) {
  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold text-[#1C2A46] mt-1 leading-tight">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ContratCard({ contrat, index, formatCurrency, onOpenDetail, onPreview, onDownload, onProlong, onEdit, onSend, onDelete, onPay, onSendForSignature, onDownloadSigned, onCopySignatureLink }) {
  // ─── Dates & statut ───
  const today = new Date();
  const dDeb = contrat.date_debut ? new Date(contrat.date_debut) : null;
  const dFin = computeDateFin(contrat);
  const msPerDay = 86400000;

  // Progression
  let pctEcoule = 0;
  let ecouleDays = 0;
  let restantDays = 0;
  let totalDays = 0;
  if (dDeb && dFin) {
    const total = dFin.getTime() - dDeb.getTime();
    const ecoule = Math.max(0, Math.min(total, today.getTime() - dDeb.getTime()));
    pctEcoule = total > 0 ? Math.round((ecoule / total) * 100) : 0;
    ecouleDays = Math.max(0, Math.round(ecoule / msPerDay));
    totalDays  = Math.max(0, Math.round(total / msPerDay));
    restantDays = Math.max(0, totalDays - ecouleDays);
  }

  // Statut : Actif / Expire bientôt (<10j) / À venir / Expiré
  let statut = 'Actif';
  let statutBg = 'bg-emerald-50 text-emerald-700';
  let barColor = 'bg-blue-500';
  let restColor = 'text-slate-500';
  if (dFin && dFin < today) {
    statut = 'Expiré';
    statutBg = 'bg-slate-100 text-slate-500';
    barColor = 'bg-slate-400';
  } else if (dDeb && dDeb > today) {
    statut = 'À venir';
    statutBg = 'bg-amber-50 text-amber-700';
    barColor = 'bg-amber-400';
  } else if (dFin && restantDays <= 10) {
    statut = 'Expire bientôt';
    statutBg = 'bg-rose-50 text-rose-700';
    barColor = 'bg-rose-500';
    restColor = 'text-rose-600 font-bold';
  }

  // Avatar entreprise (lettre + couleur stable)
  const palette = [
    'bg-orange-100 text-orange-600',
    'bg-blue-100 text-blue-600',
    'bg-rose-100 text-rose-600',
    'bg-emerald-100 text-emerald-600',
    'bg-violet-100 text-violet-600',
    'bg-amber-100 text-amber-700',
  ];
  const initial = (contrat.entreprise || '?').trim().charAt(0).toUpperCase();
  const palIdx = (contrat.entreprise || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;

  // Sous-titre : "6x Déroulant · Devant X"
  const typeLabel = contrat.type_panneau || 'Panneau';
  const nb = contrat.nombre_affiches ? `${contrat.nombre_affiches}x ` : '';
  const subtitle = `${nb}${typeLabel}${contrat.emplacement ? ' · ' + contrat.emplacement : ''}`;

  // Dates affichage
  const fmt = (d) => {
    if (!d) return 'N/A';
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString('fr-FR');
    } catch { return String(d).split('T')[0]; }
  };

  // Payé ?
  const isPaye = (contrat.statut_paiement || '').toLowerCase() === 'payé' || contrat.paiement === true || (contrat.statut || '').toLowerCase() === 'payé';

  // Formatage durée écoulée en jours (court)
  const dLabel = (days) => `${days} j`;

  const stop = (e) => e.stopPropagation();

  return (
    <Card
      className="border border-slate-100 shadow-sm hover:shadow-md transition cursor-pointer rounded-2xl bg-white"
      onClick={onOpenDetail}
      data-testid={`contrat-card-${contrat.id}`}
    >
      <CardContent className="p-4">
        {/* En-tête : avatar + société + statut */}
        <div className="flex items-start gap-3 mb-3">
          <div className={`w-9 h-9 rounded-full ${palette[palIdx]} flex items-center justify-center font-bold text-sm flex-shrink-0`}>
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-bold text-slate-900 truncate leading-tight">{contrat.entreprise}</h3>
            <p className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</p>
          </div>
          <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${statutBg} whitespace-nowrap flex-shrink-0`}>
            {statut}
          </span>
        </div>

        {/* Début (gauche) + Fin à droite au-dessus de la barre */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Début</p>
            <p className="text-sm font-bold text-slate-900 leading-tight">{fmt(contrat.date_debut)}</p>
          </div>
          <div className="text-right">
            <p className={`text-[10px] uppercase tracking-wide font-bold ${
              statut === 'Expire bientôt' ? 'text-rose-700'
              : statut === 'Expiré' ? 'text-slate-500'
              : 'text-emerald-700'
            }`}>Fin du contrat</p>
            <p className={`text-sm font-extrabold leading-tight ${
              statut === 'Expire bientôt' ? 'text-rose-700'
              : statut === 'Expiré' ? 'text-slate-600'
              : 'text-emerald-800'
            }`}>{dFin ? dFin.toLocaleDateString('fr-FR') : fmt(contrat.date_fin)}</p>
          </div>
        </div>

        {/* Barre de progression — s'écoule avec le temps */}
        <div className="relative w-full h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner">
          <div
            className={`h-full ${barColor} transition-all duration-500 relative`}
            style={{ width: `${pctEcoule}%` }}
          >
            <div className="w-full h-full bg-gradient-to-r from-white/0 to-white/25"></div>
          </div>
          {pctEcoule > 0 && pctEcoule < 100 && (
            <span
              className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-slate-700 shadow"
              style={{ left: `calc(${pctEcoule}% - 7px)` }}
              title={`${pctEcoule} % écoulé`}
            />
          )}
        </div>

        {/* Nombre total de jours sous la barre, centré */}
        <div className="flex items-center justify-center mt-1 mb-2">
          <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-[11px] font-bold text-slate-700">
            Durée totale : {totalDays > 0 ? `${totalDays} jours` : '—'}
          </span>
        </div>

        <div className="flex justify-between text-[11px] mb-3">
          <span className="text-slate-500">Écoulé : <span className="font-semibold text-slate-700">{dLabel(ecouleDays)} ({pctEcoule} %)</span></span>
          <span className={restColor}>Reste {dLabel(restantDays)}</span>
        </div>

        {/* Montant + actions */}
        <div className="flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
              {isPaye ? 'Montant payé' : 'Montant à payer'}
            </p>
            <p className="text-sm font-extrabold text-slate-900 truncate">
              {formatCurrency(contrat.montant)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0" onClick={stop}>
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${isPaye ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {isPaye ? 'Payé' : 'Impayé'}
            </span>
            <button
              onClick={onPreview}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition"
              title="Prévisualiser le contrat"
              data-testid={`preview-contrat-${contrat.id}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={onDownload}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition"
              title="Télécharger le PDF"
              data-testid={`download-contrat-${contrat.id}`}
            >
              <Paperclip className="h-3.5 w-3.5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition"
                  title="Plus d'actions"
                  data-testid={`more-contrat-${contrat.id}`}
                  onClick={stop}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" onClick={stop}>
                <DropdownMenuItem onClick={onProlong} data-testid={`prolong-contrat-${contrat.id}`}>
                  <Clock className="h-4 w-4 mr-2 text-slate-500" /> Prolonger le contrat
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onEdit} data-testid={`edit-contrat-${contrat.id}`}>
                  <Pencil className="h-4 w-4 mr-2 text-amber-500" /> Modifier
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSend} data-testid={`send-contrat-${contrat.id}`}>
                  <Send className="h-4 w-4 mr-2 text-violet-500" /> Relancer par email
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {contrat.signature_status === 'signed' ? (
                  <DropdownMenuItem onClick={onDownloadSigned} data-testid={`download-signed-${contrat.id}`}>
                    <ShieldCheck className="h-4 w-4 mr-2 text-emerald-600" /> Télécharger version signée
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={onSendForSignature} data-testid={`send-signature-${contrat.id}`}>
                      <Pen className="h-4 w-4 mr-2 text-indigo-500" />
                      {contrat.signature_status === 'pending' ? 'Renvoyer signature' : 'Envoyer pour signature'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onCopySignatureLink} data-testid={`copy-signature-link-${contrat.id}`}>
                      <Copy className="h-4 w-4 mr-2 text-purple-500" /> Copier le lien de signature
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onPay} disabled={isPaye} data-testid={`pay-contrat-${contrat.id}`}>
                  <CheckCircle className="h-4 w-4 mr-2 text-emerald-600" /> {isPaye ? 'Déjà payé' : 'Marquer comme payé'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-rose-600 focus:text-rose-700" data-testid={`delete-contrat-${contrat.id}`}>
                  <Trash2 className="h-4 w-4 mr-2" /> Annuler / Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Badges secondaires (signature) */}
        {(contrat.signature_status === 'signed' || contrat.signature_status === 'pending') && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-50" onClick={stop}>
            {contrat.signature_status === 'signed' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                <ShieldCheck className="h-3 w-3" />
                Signé {contrat.signature_signer_name ? `par ${contrat.signature_signer_name}` : ''}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700">
                <Clock className="h-3 w-3" /> Signature en attente
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ContratDetailPanel({
  open,
  onClose,
  contrat,
  formatCurrency,
  editingNotes,
  notesDraft,
  setEditingNotes,
  setNotesDraft,
  onSaveNotes,
  onModifyContract,
  onRefaireContrat,
  onProlongerContrat,
}) {
  if (!open || !contrat) return null;

  const today = new Date();
  const dDeb = contrat.date_debut ? new Date(contrat.date_debut) : null;
  const dFin = computeDateFin(contrat);

  let statut = 'Actif';
  let statutStyle = 'bg-emerald-100 text-emerald-700 border-emerald-200';
  let dotColor = 'bg-emerald-500';
  let progressColor = 'bg-blue-500';
  if (dFin && dFin < today) {
    statut = 'Expiré';
    statutStyle = 'bg-rose-100 text-rose-700 border-rose-200';
    dotColor = 'bg-rose-500';
    progressColor = 'bg-rose-500';
  } else if (dDeb && dDeb > today) {
    statut = 'À venir';
    statutStyle = 'bg-amber-100 text-amber-700 border-amber-200';
    dotColor = 'bg-amber-500';
    progressColor = 'bg-amber-500';
  }

  let pctEcoule = 0;
  let ecouleLabel = '—';
  let restantLabel = '—';
  let joursRestants = null;
  if (dDeb && dFin) {
    const total = dFin.getTime() - dDeb.getTime();
    const ecoule = Math.max(0, Math.min(total, today.getTime() - dDeb.getTime()));
    pctEcoule = total > 0 ? Math.round((ecoule / total) * 100) : 0;
    const msPerDay = 86400000;
    const ecouleDays = Math.max(0, Math.round(ecoule / msPerDay));
    const totalDays = Math.max(0, Math.round(total / msPerDay));
    const restantDays = Math.max(0, totalDays - ecouleDays);
    joursRestants = restantDays;
    const fmtDuree = (days) => {
      const m = Math.floor(days / 30);
      const j = days % 30;
      return `${m} mois ${j} jour${j > 1 ? 's' : ''}`;
    };
    ecouleLabel = `${fmtDuree(ecouleDays)}`;
    restantLabel = `${fmtDuree(restantDays)}`;
  }

  const fmt = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('fr-FR');
    } catch {
      return String(d).split('T')[0];
    }
  };

  const initial = (contrat.entreprise || '?').trim().charAt(0).toUpperCase();
  const palette = ['bg-blue-100 text-blue-600', 'bg-emerald-100 text-emerald-600', 'bg-violet-100 text-violet-600', 'bg-orange-100 text-orange-600', 'bg-rose-100 text-rose-600'];
  const palIdx = (contrat.entreprise || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;

  const numeroContrat = contrat.numero_contrat || `CT-${(contrat.id || '').slice(0, 8).toUpperCase()}`;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="contrat-detail-panel">
      <div className="flex-1 bg-black/40" onClick={onClose}></div>

      <aside className="w-full max-w-2xl bg-slate-50 h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-slate-50 z-10 px-6 py-4 flex items-center justify-between border-b border-slate-200">
          <h2 className="text-lg font-bold text-[#1C2A46]">Détails du contrat</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-200 flex items-center justify-center transition-colors"
            data-testid="close-detail-panel"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <div className={`w-14 h-14 rounded-full ${palette[palIdx]} flex items-center justify-center text-2xl font-bold flex-shrink-0`}>
                  {initial}
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-bold text-[#1C2A46] truncate">{contrat.entreprise}</h3>
                  <p className="text-sm text-slate-500 truncate">{contrat.secteur || contrat.type_contrat || '—'}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statutStyle}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
                  {statut}
                </span>
                <p className="text-xs text-slate-500 mt-2">
                  Contrat n° <span className="font-medium text-slate-700">{numeroContrat}</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-500 mb-1">Type de panneaux</p>
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-800">{contrat.type_panneau || '—'}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Montant à payer</p>
                <p className="text-base font-bold text-[#1C2A46] leading-tight">{formatCurrency(contrat.montant)}</p>
                <p className="text-xs text-emerald-600 font-medium mt-0.5">Payé</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Date de début</p>
                <p className="text-sm font-medium text-slate-800">{fmt(contrat.date_debut)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Date de fin</p>
                <p className="text-sm font-extrabold text-emerald-700">{dFin ? dFin.toLocaleDateString('fr-FR') : '—'}</p>
                {!contrat.date_fin && dFin && (
                  <p className="text-[10px] text-slate-400 italic mt-0.5">Calculée à partir de la durée</p>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-5 md:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-bold text-[#1C2A46]">Durée du contrat</h4>
                <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full border border-blue-200 bg-blue-50 text-xs font-bold text-blue-700">
                  Durée totale&nbsp;: {dDeb && dFin ? Math.max(0, Math.round((dFin - dDeb) / 86400000)) : 0} jours
                </span>
              </div>
              <div className="relative w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${progressColor} transition-all`} style={{ width: `${pctEcoule}%` }}></div>
                {pctEcoule > 0 && pctEcoule < 100 && (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white border-2 border-slate-700 shadow"
                    style={{ left: `calc(${pctEcoule}% - 7px)` }}
                  />
                )}
              </div>
              <div className="flex justify-between mt-3 text-xs">
                <div>
                  <span className="flex items-center gap-1 text-slate-600">
                    <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
                    Écoulé
                  </span>
                  <p className="font-medium text-slate-800 mt-0.5">{ecouleLabel} <span className="text-blue-600">({pctEcoule}%)</span></p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500">Restant</p>
                  <p className="font-medium text-slate-800 mt-0.5">{restantLabel} <span className="text-blue-600">({100 - pctEcoule}%)</span></p>
                </div>
              </div>
            </div>
            {joursRestants !== null && (
              <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-blue-600 mb-2" />
                <p className="text-xs text-slate-500">Fin dans</p>
                <p className="text-3xl font-bold text-[#1C2A46]">{joursRestants}</p>
                <p className="text-xs text-slate-500">jours</p>
                <p className="text-xs text-slate-400 mt-1">Soit le {dFin ? dFin.toLocaleDateString('fr-FR') : '—'}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h4 className="text-sm font-bold text-[#1C2A46] mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-slate-500" />
                Informations du contrat
              </h4>
              <div className="grid grid-cols-2 gap-y-4 gap-x-3 text-sm">
                <Field label="Société" value={contrat.entreprise} />
                <Field label="Emplacement du panneau" value={contrat.emplacement || '—'} />
                <Field label="Contact responsable" value={contrat.contact || contrat.responsable || '—'} />
                <Field label="Dimension du panneau" value={contrat.dimension || '—'} />
                <Field label="Téléphone" value={contrat.telephone || '—'} />
                <Field label="Tarif mensuel" value={contrat.tarif_mensuel ? formatCurrency(contrat.tarif_mensuel) : (contrat.duree_mois && contrat.montant ? formatCurrency(contrat.montant / contrat.duree_mois) : '—')} />
                <Field label="Email" value={contrat.email || '—'} />
                <Field label="Mode de paiement" value={contrat.mode_paiement || '—'} />
                <Field label="Adresse" value={contrat.adresse || '—'} />
                <Field label="Prochain paiement" value={contrat.prochain_paiement || '—'} />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-5">
              <h4 className="text-sm font-bold text-[#1C2A46] mb-4 flex items-center gap-2">
                <Pencil className="h-4 w-4 text-slate-500" />
                Notes
              </h4>
              {editingNotes ? (
                <div className="space-y-3">
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="w-full min-h-[140px] px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Saisissez une note..."
                    data-testid="notes-textarea"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditingNotes(false); setNotesDraft(contrat.notes || ''); }}>
                      Annuler
                    </Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={onSaveNotes} data-testid="save-notes-btn">
                      Enregistrer
                    </Button>
                  </div>
                </div>
              ) : contrat.notes ? (
                <div>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{contrat.notes}</p>
                  <Button variant="outline" size="sm" className="gap-1 mt-3" onClick={() => setEditingNotes(true)} data-testid="edit-notes-btn">
                    <Pencil className="h-3 w-3" />
                    Modifier
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                    <Pencil className="h-5 w-5 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500">Aucune note pour ce contrat.</p>
                  <p className="text-xs text-slate-400 mb-3">Cliquez sur modifier pour ajouter une note.</p>
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => setEditingNotes(true)} data-testid="add-notes-btn">
                    <Pencil className="h-3 w-3" />
                    Modifier
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-5">
            <h4 className="text-sm font-bold text-[#1C2A46] mb-4">Affiches diffusées sur ce panneau</h4>
            {Array.isArray(contrat.affiches) && contrat.affiches.length > 0 ? (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {contrat.affiches.map((aff, i) => (
                  <div key={i} className="w-44 flex-shrink-0">
                    <div className="aspect-[4/5] bg-slate-100 rounded-lg overflow-hidden">
                      {aff.image ? (
                        <img src={aff.image} alt={aff.titre || `Affiche ${i + 1}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                          <ImageIcon className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-2">Du {aff.date_debut || '—'}</p>
                    <p className="text-xs text-slate-600">Au {aff.date_fin || '—'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-slate-400 text-sm">
                <ImageIcon className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                Aucune affiche enregistrée pour ce contrat
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" className="gap-2" onClick={onModifyContract} data-testid="detail-modify-btn">
            <Pencil className="h-4 w-4" />
            Modifier le contrat
          </Button>
          <div className="flex items-center gap-2">
            <Button className="gap-2 bg-orange-500 hover:bg-orange-600 text-white" onClick={onProlongerContrat} data-testid="detail-prolonger-btn">
              <RotateCcw className="h-4 w-4 rotate-180" />
              Prolonger le contrat
            </Button>
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={onRefaireContrat} data-testid="detail-refaire-btn">
              <RotateCcw className="h-4 w-4" />
              Refaire un contrat
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm font-medium text-slate-800 mt-0.5 break-words">{value}</p>
    </div>
  );
}

